import React from "react";
import { ConsentContext, ConsentFlag } from "../../../contexts/consent.context";
import { stringJoin } from "../../../../utils/functions";
import { Button, Fade, Form, Modal } from "react-bootstrap";
import { StaticImage } from "gatsby-plugin-image";
import { OutboundLink } from "gatsby-plugin-google-gtag";

import * as Styles from "./age-gate.module.scss";

interface AgeGateState {
    declined: boolean;
    viewMore: boolean;

    allowAll: boolean;
    allowAnalytics: boolean;
    allowFunctionality: boolean;
}

const LINK_TERMS = (
    <OutboundLink className="text-white fw-bold" href="https://www.fifcousa.com/terms/?utm_soruce=geneseebeer.com&utm_medium=referral&utm_campaign=agegate" target="_blank">
        Terms & Conditions
    </OutboundLink>
);
const LINK_PRIVACY = (
    <OutboundLink className="text-white fw-bold" href="https://www.fifcousa.com/privacy-policy/?utm_soruce=geneseebeer.com&utm_medium=referral&utm_campaign=agegate" target="_blank">
        Privacy & Cookie Policy
    </OutboundLink>
);
const BUTTON_YES = "YES";
const REVIEW_DECLINED = (
    <>
        Please review our {LINK_TERMS} and {LINK_PRIVACY} if you wish you use this site.
    </>
);
const REVIEW = (
    <>
        This website uses cookies. We and/or third parties use these cookies to remember your choices and preferences while you are using our website, or when you revisit it. By
        clicking "<b>{BUTTON_YES}</b>", or using this website and continuing navigating, you accept these cookies, and agree {LINK_TERMS} and {LINK_PRIVACY}. Cookies may be changed
        in your browser settings.
    </>
);

export const AgeGateComponent: React.FC = () => {
    const { acceptAgeGate } = React.useContext(ConsentContext);

    const [state, setState] = React.useState<AgeGateState>({
        declined: false,
        viewMore: false,

        allowAll: true,
        allowAnalytics: true,
        allowFunctionality: true,
    });

    // Keep the state in sync; If the user selects "allowAll", by default that brings in functionality and analytics cookies.
    const updateAllowCookies = () => {
        const allowAll = !state.allowAll;
        if (allowAll) setState({ ...state, allowAll, allowAnalytics: true, allowFunctionality: true });
        else setState({ ...state, allowAll });
    };

    const updateAnalyticsCookies = () => {
        setState({ ...state, allowAnalytics: !state.allowAnalytics });
    };
    const updateFunctionalityCookies = () => {
        setState({ ...state, allowFunctionality: !state.allowFunctionality });
    };

    // Ensure the "submit" call is performed with proper synchronization; that is, if `allowAll` is true, then `rememberMe` is true by default.
    // However, if it is false, `rememberMe` can vary on its own.
    const confirmAgeGate = () => {
        const { allowFunctionality, allowAnalytics, allowAll } = state;

        let consentFlag: ConsentFlag;
        if (allowAll) consentFlag = ConsentFlag.AllowAll;
        else if (allowFunctionality && allowAnalytics) consentFlag = ConsentFlag.AnalyticsAndFunctionality;
        else if (allowAnalytics) consentFlag = ConsentFlag.Analytics;
        else if (allowFunctionality) consentFlag = ConsentFlag.Functionality;
        else consentFlag = ConsentFlag.Necessary;

        acceptAgeGate(consentFlag);
    };

    return (
        <Modal backdrop="static" className={Styles.ageGateBase} animation={false} keyboard={false} fullscreen={true} show aria-labelledby="age-gate-heading">
            <Modal.Body className={Styles.ageGateBody}>
                <div className={Styles.ageGateInternal}>
                    <div className="d-flex justify-content-center flex-column" hidden={state.declined}>
                        <StaticImage src="../../../images/logos/logo.png" alt="Genesee Brewery Logo" objectFit="contain" className="m-auto w-50 px-md-5" />
                    </div>
                    <h1 id="age-gate-heading" className="text-center text-uppercase fw-bold fs-1 sans-serif my-3">
                        {state.declined ? "Sorry, you're not old enough." : "Good Times for All Times await if you're 21 years or older. Are you?"}
                    </h1>
                    <div className={stringJoin(Styles.buttonRow, state.declined ? "d-none" : null)}>
                        <Button className="btn-lg mx-3" onClick={confirmAgeGate} type="button">
                            {BUTTON_YES}
                        </Button>
                        <Button className="btn-lg mx-3" onClick={() => setState({ ...state, declined: true })} type="button">
                            NO
                        </Button>
                    </div>
                    <p className="text-center fs-6 pt-3">{state.declined ? REVIEW_DECLINED : REVIEW}</p>
                    <div className={stringJoin("d-flex flex-column gap-2 fw-bold mx-auto px-3", Styles.cookieContainer, state.declined ? "d-none" : null)}>
                        <div className="mx-auto">
                            <Form.Switch
                                id="id_allow_all_switch"
                                label={state.allowAll ? "Allow All Cookies" : "Customized Cookies"}
                                checked={state.allowAll}
                                onChange={updateAllowCookies}
                            />
                        </div>
                        <div className="me-auto" hidden={state.allowAll}>
                            <Form.Switch
                                id="strictly-necessary-switch"
                                readOnly={true}
                                checked={true}
                                label={
                                    <>
                                        Strictly Necessary Cookies
                                        <br />
                                        <small>(Cannot be disabled)</small>
                                    </>
                                }
                            />
                        </div>
                        <div className="me-auto" hidden={state.allowAll}>
                            <Form.Switch id="performance-switch" onChange={() => updateAnalyticsCookies()} checked={state.allowAnalytics} label="Performance / Analytics Cookies" />
                        </div>
                        <div className="me-auto" hidden={state.allowAll}>
                            <Form.Switch id="functionality-switch" onChange={() => updateFunctionalityCookies()} checked={state.allowFunctionality} label="Functionality Cookies" />
                        </div>
                        {!state.viewMore ? (
                            <Button className="mx-auto border-0 text-decoration-underline" variant="outline-light" onClick={() => setState({ ...state, viewMore: true })}>
                                More Information
                            </Button>
                        ) : null}
                    </div>
                    <Fade in={state.viewMore && !state.declined} unmountOnExit={true} mountOnEnter={true} className="mb-auto">
                        <div className={Styles.privacyPolicy}>
                            <p className="pt-3">
                                <b>
                                    <u>Strictly Necessary Cookies</u>
                                </b>
                                <br />
                                <small>
                                    These cookies are essential to enable you to browse our Site and use its features. The information collected by these cookies relate to the
                                    operation of our Site (e.g., website scripting language and security tokens to maintain secure areas of our Site).
                                </small>
                            </p>
                            <p>
                                <b>
                                    <u>Performance/Analytics Cookies</u>
                                </b>
                                <br />
                                <small>
                                    These cookies collect anonymous information about how you use our Site (e.g., which pages you visit most often, whether you receive any error
                                    messages, and how you arrived at our Site). Information collected by these cookies is used only to improve your use of our Site and never to
                                    identify you.
                                </small>
                            </p>
                            <p>
                                <b>
                                    <u>Functionality Cookies</u>
                                </b>
                                <br />
                                <small>
                                    These cookies remember choices you make (e.g., the country you visit our Services from, your language and any changes you have made to text size
                                    or other parts of web pages that you can customize), to improve your experience of our Services and to make your visits and use more tailored
                                    and enjoyable. The information these cookies collect may be anonymized and cannot be used to track your browsing activity on other websites.
                                </small>
                            </p>
                        </div>
                    </Fade>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default AgeGateComponent;
